import { Box, Chip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import { useDispatch, useSelector } from 'react-redux'
import { columnsLearningContinuum, getFilterCurriculumElementsDR } from '../../utils'
import { renderElementsTableLC } from '../../utils.jsx'
import DataGrid from '../../../../table/DataGrid'
import { updateColumnTableCurriculum } from '../../../../../store/actions/datatable.actions'
import { useParams } from 'react-router-dom'
import AddLearningContinuum from './AddLearningContinuum'
import { updateFilter } from '../../../../../store/actions/filter.actions'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import PreviewLearningContinuum from './PreviewLearningContinuum'
import useLearningContinuum from '../../../../../hooks/developmental-rubrics/useLearningContinuum'

const FormLearningContinuum = ({ onChangeAlert, isLoading }) => {
  const { curriculumId, id } = useParams()
  const dispatch = useDispatch()
  const formData = useSelector((state) => state?.dr?.form)
  const oldFormData = useSelector((state) => state?.dr?.oldForm || {})
  const formError = useSelector((state) => state?.dr?.formError || {})
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})

  const [form, setForm] = useState(formData)
  const [showDialog, setShowDialog] = useState(null)
  const [showConfirm, setShowConfirm] = useState(null)

  const list = useSelector((state) => (state?.lc ? state?.lc[curriculumId]?.list || [] : []))
  const columnsTable = useSelector((state) => state?.datatable?.lc?.[curriculumId] || [])

  const {
    isLoading: isLoadingSave,
    insertLearningContinuum,
    removeLearningContinuum
  } = useLearningContinuum()
  const onAdd = (lcs) => {
    if (id) {
      const newLcs = lcs.map((lc) => {
        const find = oldFormData?.lcs?.find((item) => String(item._id) === String(lc._id))
        if (find) {
          return find
        }

        const granularSteps = lc.granularSteps
          ? lc.granularSteps
          : lc.levels.map((level) => {
              return {
                id: `level-${level.levelId}-gs-0`,
                levelId: level.levelId,
                isLevel: true,
                definition: level.definition,
                workedExamples: level.workedExamples || [],
                teachingSuggestions: level.teachingSuggestions || []
              }
            })

        return {
          _id: lc._id,
          curriculumElements: lc.curriculumElements,
          lcElements: lc.lcElements,
          granularSteps,
          version: lc.version
        }
      })
      insertLearningContinuum(newLcs)
    } else {
      const newLcs = lcs.map((lc) => {
        const find = oldFormData?.lcs?.find((item) => String(item._id) === String(lc._id))
        if (find) {
          return find
        }

        const granularSteps = lc.granularSteps
          ? lc.granularSteps
          : lc.levels.map((level) => {
              return {
                id: `level-${level.levelId}-gs-0`,
                levelId: level.levelId,
                isLevel: true,
                definition: level.definition,
                workedExamples: level.workedExamples || [],
                teachingSuggestions: level.teachingSuggestions || []
              }
            })

        return {
          _id: lc._id,
          curriculumElements: lc.curriculumElements,
          lcElements: lc.lcElements,
          granularSteps,
          version: lc.version
        }
      })

      onChangeAlert('lcs', [...form.lcs, ...newLcs])
    }
  }

  const onRemoveConfirm = (row) => {
    const find = oldFormData?.lcs?.find((item) => item._id === row._id)
    if (find) {
      setShowConfirm(row)
    } else {
      onRemove(row._id)
    }
  }
  const onRemove = (lcId) => {
    if (id) {
      removeLearningContinuum([{ _id: lcId }])
    } else {
      const newLcs = form.lcs.filter((item) => item._id !== lcId)
      onChangeAlert('lcs', newLcs)
    }
  }
  const onOpenModal = () => {
    setShowDialog({ type: 'add' })
    const filter = getFilterCurriculumElementsDR(formData.curriculumElements || {}, currentCurriculum)
    dispatch(updateFilter('lc', filter))
  }

  useEffect(() => {
    if (formData) {
      const lcsIds = formData?.lcs?.map((lc) => lc._id) || []
      setForm({
        ...formData,
        lcsIds
      })
      const filter = getFilterCurriculumElementsDR(formData.curriculumElements || {}, currentCurriculum)
      dispatch(updateFilter('lc', filter))
    }
  }, [formData])

  useEffect(() => {
    const columns = columnsLearningContinuum(currentCurriculum, list)
    dispatch(updateColumnTableCurriculum(currentCurriculum._id, 'lc', columns, true))
  }, [currentCurriculum])

  return (
    <>
      <Box
        className={[
          'px-4 py-2 h-6 flex items-center justify-between bg-primary text-primary-foreground'
        ].join(' ')}
      >
        <div className='flex gap-4 items-center'>
          <Typography variant='inherit' className='font-bold capitalize' component='p'>
            Add / Remove Learning Continuum
          </Typography>
          {formError.lcs && (
            <Typography component={'span'} color='error' className='text-sm px-2 py-1 rounded bg-white'>
              {formError.lcs}
            </Typography>
          )}
        </div>
        <div>
          <IconTooltipButton
            title='Add Learning Continuum'
            icon='plus'
            classNameIcon='text-lg'
            style={{
              borderRadius: '0.5rem',
              padding: '0.45rem',
              color: 'var(--primary)',
              backgroundColor: '#fff',
              width: 25,
              height: 25
            }}
            className='shadow-sm'
            onClick={onOpenModal}
          />
        </div>
      </Box>
      <Box className='absolute top-36 bottom-0 inset-x-4'>
        <DataGrid
          id='add-remove-learning-continuum'
          showSetting={false}
          curriculumId={curriculumId}
          rows={form?.lcs?.map((row, index) => {
            const elements = renderElementsTableLC(row, currentCurriculum, 'lc')
            return {
              ...row,
              index: index + 1,
              version: (
                <Chip label={`v${row.version || '1'}`} size='small' color='primary' className='font-bold' />
              ),
              ...elements,
              actions: (
                <div className='flex items-center gap-1 items-center justify-center'>
                  <IconTooltipButton
                    id={`preview-${row._id}`}
                    disabled={isLoading || isLoadingSave}
                    title='Preview'
                    icon='expand-alt'
                    onClick={() => setShowDialog({ type: 'preview', form: row })}
                  />
                  <IconTooltipButton
                    id={`delete-${row._id}`}
                    disabled={isLoading || isLoadingSave}
                    title='Delete'
                    icon='trash-alt'
                    onClick={() => onRemoveConfirm(row)}
                  />
                </div>
              )
            }
          })}
          rowsPerPage={form?.lcs?.length || 0}
          columns={[
            { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
            ...columnsTable,
            { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
          ]}
          labelNoRows="You don't have any learning continuum yet"
        />
      </Box>
      {showDialog?.type === 'add' && (
        <AddLearningContinuum
          open={showDialog.type === 'add'}
          onClose={() => setShowDialog(null)}
          lcsIds={form.lcsIds}
          onAdd={onAdd}
        />
      )}
      {showDialog?.type === 'preview' && (
        <PreviewLearningContinuum
          open={showDialog.type === 'preview'}
          onClose={() => setShowDialog(null)}
          formData={showDialog.form}
        />
      )}
      {showConfirm && (
        <DialogConfirmCancel
          open={!!showConfirm}
          onCancel={() => setShowConfirm(null)}
          onConfirm={() => {
            onRemove(showConfirm._id)
            setShowConfirm(null)
          }}
          title='WARNING'
          maxWidth='xs'
        >
          <Typography className='text-muted-foreground opacity-90'>
            by deleting the Learning Continuum:
            {Object.values(showConfirm?.lcElements || {}).map((item) => (
              <b key={item} className='px-1'>
                {item}
              </b>
            ))}
            you are deleting any marking that has been done for this Learning Continuum in this Developmental
            Rubric
          </Typography>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogConfirmCancel>
      )}
    </>
  )
}

export default FormLearningContinuum
